import { MouseEvent, useState } from 'react';
import { Copy, CopyCheck } from 'lucide-react';
import Papa from 'papaparse';
import { cn } from 'src/shared/lib/utils';
import IconButton from 'src/shared/components/ui/buttons/icon-button';
import mixpanel from 'src/shared/lib/mixpanel';

/**
 *
 * Copy button that uses the deprecated execCommand('copy')-command.
 * Needed because Outlook on the web does not yet support the Clipboard API.
 *
 * https://techcommunity.microsoft.com/idea/microsoft365developerplatform/outlook-addin---enable-clipboard-api/2626853
 *
 */

/**
 * Converts CSV string to TSV (tab-separated values) format
 */
function convertCsvToTsv(csvString: string): string {
  try {
    const parsed = Papa.parse<string[]>(csvString, {
      header: false,
      skipEmptyLines: false,
    });

    // Convert each row to tab-separated format
    return parsed.data.map((row) => row.join('\t')).join('\n');
  } catch {
    // If parsing fails, assume it's not CSV and return original
    return csvString;
  }
}

/**
 * Detects if a string is likely CSV format
 */
function isLikelyCsv(value: string): boolean {
  // Simple heuristic: CSV typically has commas and newlines
  // and doesn't have tabs (which would indicate TSV)
  return value.includes(',') && value.includes('\n') && !value.includes('\t');
}

type CopyButtonProps = {
  value: string;
  variant?: 'default' | 'subtle';
  className?: string;
};

export default function CopyButton({ value, variant = 'default', className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();

    // Convert CSV to TSV if it looks like CSV
    const valueToCopy = isLikelyCsv(value) ? convertCsvToTsv(value) : value;

    const textarea = document.createElement('textarea');
    textarea.value = valueToCopy;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
    setCopied(true);
    setTimeout(() => setCopied(false), 5000);
    mixpanel.track('Outlook Add-In - Copied value');
  }

  if (variant === 'subtle') {
    return (
      <button
        type="button"
        disabled={!value}
        onClick={handleCopy}
        className={cn(
          'shrink-0 disabled:opacity-50 disabled:pointer-events-none',
          !copied ? 'hover:text-neutral-800 text-neutral-400 transition-all' : ' ',
          className
        )}
      >
        {!copied ? <Copy size={12} /> : <CopyCheck size={12} className="text-success hover:text-success-800" />}
      </button>
    );
  }

  return (
    <IconButton icon={copied ? 'CopyCheck' : 'Copy'} variant={copied ? 'success' : 'light'} onClick={handleCopy} />
  );
}
